import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Phone, Mail, Award, ShieldCheck, ThumbsUp, ArrowRight } from 'lucide-react'

export default function ViUdfoerer() {
  // SEO Focus
  useEffect(() => {
    document.title = 'Vi udfører | Professionelt malerarbejde i Østjylland | Malerfirma Villy Poulsen'
    const metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute(
        'content',
        'Uanset om du står over for en mindre opfriskning eller en større renoveringsopgave, kan du regne med et professionelt resultat og en løsning, der er tilpasset dine behov.'
      )
    }
  }, [])

  const ydelser = [
    { label: 'Indvendigt malerarbejde', href: '/indvendig-maling' },
    { label: 'Udvendig maling af hus og facade, træværk og vinduer mm.', href: '/udvendig-maling' },
    { label: 'Facademaling', href: '/facademaling' },
    { label: 'Højtryksrensning af forskellige emner', href: '/hoejtryksrensning' },
    { label: 'Sprøjtemaling af bla: Køkkner, møbler, lofter, radiatorer mm.', href: '/sproejtemaling' },
    { label: 'Fuldspartling', href: '/fuldspartling' },
    { label: 'Opsætning af filt og tapet', href: '/opsaetning-af-filt-og-tapet' },
    { label: 'Renovering og vedligeholdelse', href: '/renovering-og-vedligeholdelse' },
    { label: 'Forsikringsskader', href: '/forsikringsskader' },
    { label: 'Malerarbejde for erhverv og boligforeninger', href: '/erhverv' },
    { label: 'Offentlige og private opgaver', href: '/vi-udfoerer' }
  ]

  const kundegrupper = [
    { label: 'Private kunder', href: '/vi-udfoerer' },
    { label: 'Erhvervs kunder', href: '/erhverv' },
    { label: 'Boligforeninger', href: '/erhverv' },
    { label: 'Private boligselskaber', href: '/erhverv' },
    { label: 'Institutioner', href: '/erhverv' },
    { label: 'Licitations opgaver', href: '/erhverv' },
    { label: 'Offentlige opgaver', href: '/erhverv' }
  ]

  return (
    <div className="bg-white">
      {/* Hero-sektion */}
      <section className="relative bg-[#20252C] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00020_3.jpeg"
            alt="Professionelt malerarbejde i Østjylland"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-gray-900/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#01529E]/90 text-white text-xs font-bold uppercase tracking-widest mb-6">
              Vores Ydelser
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
              Alt i professionelt malerarbejde i Østjylland
            </h1>
            <p className="mt-4 text-xl text-[#ED1C26] font-bold">
              Erfaren maler til både private og erhverv
            </p>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl">
              Uanset om du står over for en mindre opfriskning eller en større renoveringsopgave, kan du regne med et professionelt resultat og en løsning, der er tilpasset dine behov.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 rounded-xl bg-[#01529E] text-white font-bold text-center hover:bg-[#01529E]/90 transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer"
              >
                Kontakt os i dag
              </Link>
              <a
                href="tel:40637670"
                className="px-6 py-3.5 rounded-xl bg-white/10 text-white font-bold text-center hover:bg-white/20 transition-all border border-white/10 flex items-center justify-center gap-2"
              >
                Ring 40 63 76 70
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Vi udfører alle typer malerarbejde */}
      <section className="py-20 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#01529E]/10 text-[#01529E] text-xs font-bold uppercase tracking-widest">
                Hvad vi tilbyder
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4 leading-tight">
                Vi udfører alle typer malerarbejde
              </h2>
              <p className="mt-4 text-gray-600 text-lg leading-relaxed">
                Med mange års erfaring i branchen kan vi håndtere både klassiske og specialiserede maleropgaver for både private, erhverv og offentlige institutioner.
              </p>

              {/* Værdipunkter / Fordele */}
              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-[#01529E]/5 text-[#01529E] shrink-0">
                    <Award className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Høj faglig stolthed</h4>
                    <p className="text-sm text-gray-600">Vi går aldrig på kompromis med detaljerne og leverer altid et perfekt resultat.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-[#ED1C26]/5 text-[#ED1C26] shrink-0">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Tryghed & Garanti</h4>
                    <p className="text-sm text-gray-600">Som professionelt malerfirma sikrer vi dig ordentlig rådgivning og holdbare løsninger.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-[#01529E]/5 text-[#01529E] shrink-0">
                    <ThumbsUp className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Overholdte aftaler</h4>
                    <p className="text-sm text-gray-600">Vi kommer til tiden, rydder op efter os selv og afleverer til den aftalte pris.</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 hidden lg:block group overflow-hidden rounded-2xl border border-gray-200/60 shadow-md">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/05819e56-de71-4634-83f6-601b6e24fc89.jpeg"
                  alt="Malerarbejde i Østjylland"
                  className="object-cover w-full h-72 transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-8">
              {/* Ydelser Card */}
              <div className="relative bg-white rounded-2xl border border-gray-200/60 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                {/* Dekorativ venstre-bar */}
                <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-gradient-to-b from-[#01529E] to-[#ED1C26]" />
                
                <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4 pl-2">
                  <div className="p-2 rounded-lg bg-[#01529E]/5 text-[#01529E]">
                    <Award className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                      Vores malerydelser
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Klik på en ydelse for at læse mere</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-2">
                  {ydelser.map((ydelse, idx) => (
                    <Link 
                      key={idx} 
                      to={ydelse.href}
                      className="group flex items-center justify-between p-3 rounded-xl bg-gray-50/50 border border-gray-100 hover:bg-white hover:border-[#01529E]/20 hover:shadow-sm transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="rounded-full p-1 bg-white border border-gray-100 group-hover:border-[#ED1C26]/20 transition-colors shrink-0">
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#ED1C26] transition-transform duration-200 group-hover:scale-110" />
                        </div>
                        <span className="text-sm text-gray-700 font-bold leading-tight group-hover:text-[#01529E] transition-colors truncate">
                          {ydelse.label.split(' af bla:')[0].split(' af hus')[0]}
                        </span>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Kundegrupper Card */}
              <div className="relative bg-white rounded-2xl border border-gray-200/60 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                {/* Dekorativ venstre-bar */}
                <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-gradient-to-b from-[#ED1C26] to-[#01529E]" />
                
                <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4 pl-2">
                  <div className="p-2 rounded-lg bg-[#ED1C26]/5 text-[#ED1C26]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                      Hvem vi hjælper
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Vi løser opgaver for alle typer kunder</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-2">
                  {kundegrupper.map((gruppe, idx) => (
                    <Link 
                      key={idx} 
                      to={gruppe.href}
                      className="group flex items-center justify-between p-3 rounded-xl bg-gray-50/50 border border-gray-100 hover:bg-white hover:border-[#ED1C26]/20 hover:shadow-sm transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="rounded-full p-1 bg-white border border-gray-100 group-hover:border-[#01529E]/20 transition-colors shrink-0">
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#01529E] transition-transform duration-200 group-hover:scale-110" />
                        </div>
                        <span className="text-sm text-gray-700 font-bold leading-tight group-hover:text-[#ED1C26] transition-colors truncate">
                          {gruppe.label}
                        </span>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Kvalitet fra start til slut */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00017_3.jpeg"
                alt="Kvalitets malerarbejde"
                className="rounded-2xl shadow-xl border border-gray-100 object-cover w-full h-[450px]"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Kvalitet & Tillid
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-2">
                Professionelt forløb og høj kvalitet
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Hos Malerfirma Villy Poulsen ApS går vi aldrig på kompromis med kvaliteten. Vi ved, at godt malerarbejde handler om mere end maling. Det handler også om tillid, god dialog og et professionelt forløb fra start til slut.
                </p>
                <p>
                  Vi samarbejder desuden med dygtige håndværkere inden for andre fagområder og kan derfor hjælpe med koordinering og projektstyring, hvis din opgave kræver flere håndværksfag. Det giver dig en mere enkel og effektiv proces med én fast kontaktperson gennem hele projektet.
                </p>
                <p>
                  Vi lægger stor vægt på at overholde vores aftaler og levere høj kvalitet til konkurrencedygtige priser. Hos os får du ærlig rådgivning, godt håndværk og løsninger, der holder i mange år.
                </p>
                <p className="font-semibold text-gray-900">
                  Vi sætter en ære i tilfredse kunder og arbejder hver dag for at være et foretrukket malerfirma i Østjylland.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA-sektion */}
      <section className="bg-[#20252C] text-white py-16 relative overflow-hidden">
        <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 w-96 h-96 bg-[#01529E]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4">
            Skal vi hjælpe med dit næste malerprojekt?
          </h2>
          <p className="text-gray-300 text-sm sm:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            Kontakt Malerfirma Villy Poulsen ApS i dag for en uforpligtende samtale og et godt tilbud på dit malerarbejde i Østjylland.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-sm">
            <a href="tel:40637670" className="flex items-center gap-2 hover:text-[#ED1C26] transition-colors font-bold text-lg">
              <Phone className="h-5 w-5 text-[#ED1C26]" />
              Telefon: 40 63 76 70
            </a>
            <a href="mailto:mail@malerfirma-villy-poulsen.dk" className="flex items-center gap-2 hover:text-[#ED1C26] transition-colors font-bold text-lg">
              <Mail className="h-5 w-5 text-[#ED1C26]" />
              E-mail: mail@malerfirma-villy-poulsen.dk
            </a>
          </div>
          <div className="mt-8">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#01529E] text-white font-bold hover:bg-[#01529E]/90 transition-all shadow-lg cursor-pointer"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
